// Upload step (Document Scanner landing) — the first screen of the OCR flow.
// Extracted from pages/InvoiceOCR.jsx.
//
// Owns:
//   • The doc-type chooser + AI-paywall banner + drag/drop dropzone
//   • The 3 demo CTAs (1040 / W-9 / Contract)
//   • The "Recent scans" panel with filter chips + Move-to overflow menu
//   • The custom folder picker dialog
//
// Hands the job_id back up via `onUploadComplete(jobId)` once a real upload
// completes or the user clicks a Recent Scan row.
import { useState, useEffect, useCallback, useRef } from 'react';
import { useWorkspace } from '../../context/WorkspaceContext';
import { apiFetch } from '../../fetchInterceptor';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from '../ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  Upload, FileText, Loader2, Sparkles, Clock, CheckCircle, MoreHorizontal,
  ArrowRight, Folder, Eye, Trash2, Layers, Wand2,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { DOC_TYPE_CHOICES, FILING_DESTINATIONS } from './ocrConstants';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const UploadStep = ({ documentType, onUploadComplete }) => {
  const { currentWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [demoLoading, setDemoLoading] = useState(null);
  const fileInputRef = useRef(null);
  // User-selected document type — defaults to "auto" (AI decides) unless
  // they navigated in via a typed URL like ?type=tax_return.
  const KNOWN_TYPES = DOC_TYPE_CHOICES.map((c) => c.value);
  const initialType = KNOWN_TYPES.includes(documentType) && documentType !== 'bill'
    ? documentType
    : 'auto';
  const [selectedType, setSelectedType] = useState(initialType);
  const selectedChoice = DOC_TYPE_CHOICES.find((c) => c.value === selectedType) || DOC_TYPE_CHOICES[0];

  // ─── AI access gate ────────────────────────────────────────────────
  // Trial users can see the UI but can't burn AI credits. Pre-fetch the
  // gate status so we can swap the dropzone CTA + disable real uploads
  // (demo samples still work — they're pre-canned, no AI cost).
  const [aiAccess, setAiAccess] = useState({ ai_unlocked: true });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiFetch(`${API_URL}/api/billing/ai-access`);
        if (r.ok) {
          const data = await r.json();
          if (!cancelled) setAiAccess(data);
        }
      } catch {
        // Silent — assume unlocked rather than annoying paying users with
        // a false locked banner on transient network errors.
      }
    })();
    return () => { cancelled = true; };
  }, []);
  const aiLocked = aiAccess && aiAccess.ai_unlocked === false;

  // Recent scans for this workspace — shown below the dropzone so users
  // can find documents they've previously processed without leaving the
  // Document Scanner. Last 12 jobs, with filter chips for filed/unfiled
  // and destination so users can find any scan in one click.
  const [recentJobs, setRecentJobs] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);
  // Filter chip — 'all' | 'unfiled' | 'tax_folder' | 'receipts' | 'banking' | 'vendors' | 'documents'
  const [recentFilter, setRecentFilter] = useState('all');
  const loadRecent = useCallback(async () => {
    if (!currentWorkspace?.workspace_id) return;
    setRecentLoading(true);
    try {
      const params = new URLSearchParams({
        workspace_id: currentWorkspace.workspace_id,
        limit: '12',
      });
      if (recentFilter === 'unfiled') params.append('filed', 'false');
      else if (recentFilter !== 'all') {
        params.append('filed', 'true');
        params.append('destination', recentFilter);
      }
      const r = await apiFetch(`${API_URL}/api/ocr/jobs/list?${params.toString()}`);
      if (r.ok) {
        const data = await r.json();
        setRecentJobs(data.jobs || []);
      }
    } catch {
      // swallow — recent-scans panel is a convenience, never block uploads
    } finally {
      setRecentLoading(false);
    }
  }, [currentWorkspace?.workspace_id, recentFilter]);
  useEffect(() => { loadRecent(); }, [loadRecent]);

  // Auto-poll Recent Scans every 8 s ONLY while at least one scan is
  // still in-progress. Stops polling the moment everything is done, so
  // we don't hammer the server forever. This is what makes the row
  // visibly flip from "Processing" → filed-badge without a refresh.
  useEffect(() => {
    const anyInFlight = recentJobs.some((j) =>
      ['pending', 'processing', 'preparing'].includes(j.status));
    if (!anyInFlight) return;
    const id = setInterval(loadRecent, 8000);
    return () => clearInterval(id);
  }, [recentJobs, loadRecent]);

  // Refile (move scan to a different destination). Used by the "Move to…"
  // overflow menu on each Filed scan row.
  const refileScan = async (jobId, newDestination, folderIdOverride = null) => {
    try {
      const r = await apiFetch(`${API_URL}/api/ocr/${jobId}/refile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          destination: newDestination,
          ...(folderIdOverride ? { folder_id: folderIdOverride } : {}),
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Move failed.');
      }
      const result = await r.json();
      toast.success(result.message || 'Moved');
      loadRecent();
    } catch (e) {
      toast.error(e.message || 'Move failed.');
    }
  };

  // ─── Delete from queue (filed scans hide, unfiled scans delete) ─────
  // The 3-dots menu on each Recent Scans row has a Delete option. If
  // the scan has been filed to a folder, we soft-hide it from the queue
  // but the doc stays in the Documents Hub. If it was never filed, the
  // OCR job is hard-deleted.
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const deleteJob = async () => {
    if (!deleteCandidate) return;
    setDeleting(true);
    try {
      const r = await apiFetch(`${API_URL}/api/ocr/${deleteCandidate.job_id}`, {
        method: 'DELETE',
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Delete failed.');
      }
      const data = await r.json();
      toast.success(data.message || 'Removed from Recent Scans.');
      setDeleteCandidate(null);
      loadRecent();
    } catch (e) {
      toast.error(e.message || 'Delete failed.');
    } finally {
      setDeleting(false);
    }
  };

  // ─── Auto-split-file (Phase 5) ──────────────────────────────────────
  // When a multi-doc PDF is detected we surface a "We found N docs"
  // callout for the user. Clicking "File all" creates N child OCR jobs
  // and routes each one to its detected destination.
  const [splitJob, setSplitJob] = useState(null);
  const [splitFiling, setSplitFiling] = useState(false);
  const autoSplitFile = async (job) => {
    if (!job?.job_id) return;
    setSplitFiling(true);
    try {
      const r = await apiFetch(`${API_URL}/api/ocr/${job.job_id}/auto-split-file`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Could not split this PDF.');
      }
      const data = await r.json();
      toast.success(`Filed ${data.filed_count} document${data.filed_count === 1 ? '' : 's'}.`);
      setSplitJob(null);
      loadRecent();
    } catch (e) {
      toast.error(e.message || 'Auto-split failed.');
    } finally {
      setSplitFiling(false);
    }
  };

  // ─── Folder Picker (Phase E) ────────────────────────────────────────
  // Lets users move a filed scan into ANY existing firm folder, not just
  // the 5 preset destinations. Opened from the "Pick another folder…"
  // item in the Move-to dropdown.
  const [folderPickerJob, setFolderPickerJob] = useState(null);
  const [availableFolders, setAvailableFolders] = useState([]);
  const [folderPickerLoading, setFolderPickerLoading] = useState(false);
  const [folderPickerSearch, setFolderPickerSearch] = useState('');
  const openFolderPickerFor = async (job) => {
    setFolderPickerJob(job);
    setFolderPickerSearch('');
    setFolderPickerLoading(true);
    try {
      const r = await apiFetch(
        `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/folders?space=firm`
      );
      if (r.ok) {
        const data = await r.json();
        setAvailableFolders(data.folders || data || []);
      } else {
        setAvailableFolders([]);
      }
    } catch {
      setAvailableFolders([]);
    } finally {
      setFolderPickerLoading(false);
    }
  };
  const moveToCustomFolder = async (folderId) => {
    if (!folderPickerJob) return;
    // We still pass a destination (use the job's current one so backend
    // rule-record bookkeeping has a meaningful tag) but the folder_id
    // override is what determines where the doc actually lands.
    const dest = folderPickerJob.filed_destination || 'documents';
    await refileScan(folderPickerJob.job_id, dest, folderId);
    setFolderPickerJob(null);
  };

  // Spawns a demo OCR job from a bundled sample and lands the user on the
  // appropriate "magic" surface. Tax returns deep-link straight into the
  // Phase 4 savings report; W-9 + Contract surface in the OCR review screen
  // with their respective Smart Actions chips lit up.
  const tryDemo = async (sample) => {
    if (!currentWorkspace) {
      toast.error('Select a workspace first.');
      return;
    }
    setDemoLoading(sample);
    try {
      const r = await apiFetch(`${API_URL}/api/ocr/demo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sample, workspace_id: currentWorkspace.workspace_id }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Demo failed to load.');
      }
      const d = await r.json();
      toast.success(`Loaded a sample ${d.doc_type.replace('_', ' ')} — explore the AI features.`);
      if (d.doc_type === 'tax_return') {
        navigate(`/ocr/${d.job_id}/tax-report`);
      } else {
        onUploadComplete(d.job_id);
      }
    } catch (e) {
      toast.error(e.message || 'Demo failed.');
    } finally {
      setDemoLoading(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleFileUpload = async (file) => {
    if (aiLocked) {
      toast.error(
        <div>
          <div className="font-semibold">AI features require a paid plan</div>
          <div className="text-xs">Upgrade to Starter ($19.99/mo) to scan real documents. Demo samples below stay free.</div>
        </div>,
        { duration: 6000, action: { label: 'Upgrade', onClick: () => navigate('/billing') } },
      );
      return;
    }
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    const allowedExtensions = ['.pdf', '.png', '.jpg', '.jpeg'];

    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(ext)) {
      toast.error('Please upload a PDF or image file (PNG, JPG)');
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      toast.error('File size must be less than 50MB');
      return;
    }

    setUploading(true);
    setUploadProgress(10);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', selectedType);

      setUploadProgress(30);

      const response = await apiFetch(
        `${API_URL}/api/ocr/upload?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'POST',
          body: formData
        }
      );

      setUploadProgress(70);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }

      const result = await response.json();
      setUploadProgress(100);

      if (result.status === 'duplicate_file') {
        toast.info('This file has already been processed');
        onUploadComplete(result.existing_job_id || result.job_id);
      } else {
        // Don't navigate the user away. The scan now runs entirely in the
        // background — the top-bar bell will ping them when AI Buck
        // finishes, and the "Recent scans" panel below the dropzone will
        // show the in-progress job. This is the fix for the "page keeps
        // blinking while scanning" complaint.
        toast.success(
          <div className="flex flex-col gap-1">
            <span className="font-semibold">Scan started — you can keep working.</span>
            <span className="text-xs text-muted-foreground">
              The bell in the top bar will ping you the moment it&apos;s ready.
            </span>
          </div>,
          {
            duration: 6000,
            action: {
              label: 'View scan',
              onClick: () => onUploadComplete(result.job_id),
            },
          },
        );
        // Surface the in-progress scan in the Recent Scans panel right
        // away so the user has something to click into if they want to
        // poke at it while it runs.
        loadRecent();
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Failed to upload document');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <>
    <Card className="max-w-2xl mx-auto border-2">
      <CardHeader className="text-center pb-2">
        <CardTitle className="flex items-center justify-center gap-2 text-2xl">
          <Sparkles className="w-6 h-6 text-violet-600" />
          Drop anything. AI Buck reads it.
        </CardTitle>
        <CardDescription className="mt-2 text-base">
          Bills · Invoices · Receipts · Tax returns · W-9s · Contracts · Bank statements
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Doc-type chooser — lets users tell AI Buck exactly what they're
            uploading so routing is never wrong. "Auto-detect" preserves
            the old behavior for power users. */}
        <div className="mb-4 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end" data-testid="doc-type-chooser">
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5 block">
              I&apos;m uploading…
            </Label>
            <Select value={selectedType} onValueChange={setSelectedType}>
              <SelectTrigger className="w-full" data-testid="doc-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DOC_TYPE_CHOICES.map((c) => (
                  <SelectItem key={c.value} value={c.value} data-testid={`doc-type-option-${c.value}`}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div
            className="rounded-lg bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-900 px-3 py-2.5 text-xs text-violet-900 dark:text-violet-200"
            data-testid="doc-type-destination-hint"
          >
            <div className="text-[10px] uppercase tracking-wide opacity-70">Will be saved to</div>
            <div className="font-semibold">{selectedChoice.destLabel}</div>
          </div>
        </div>

        {aiLocked && (
          <div
            className="mb-4 rounded-xl border-2 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 p-4 flex items-start gap-3"
            data-testid="ai-locked-banner"
          >
            <Sparkles className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-amber-900 dark:text-amber-100 text-sm">
                {aiAccess.gate_reason === 'AI_TRIAL_LOCKED'
                  ? 'AI Buck unlocks when your trial converts'
                  : 'Unlock AI Buck to scan real documents'}
              </div>
              <div className="text-xs text-amber-800 dark:text-amber-200 mt-1">
                {aiAccess.message ||
                  'Upgrade to Starter ($19.99/mo) to use the Document Scanner, Ask AI, and Tax Savings Review on your own documents. Demo samples below are always free.'}
              </div>
            </div>
            <Button
              size="sm"
              className="bg-amber-600 hover:bg-amber-700 text-white flex-shrink-0"
              onClick={() => navigate(aiAccess.upgrade_path || '/billing')}
              data-testid="ai-locked-upgrade-btn"
            >
              Upgrade
            </Button>
          </div>
        )}

        <div
          className={cn(
            "border-2 border-dashed rounded-xl p-12 text-center transition-all cursor-pointer",
            isDragging && !aiLocked && "border-violet-500 bg-violet-50/50 dark:bg-violet-950/20 scale-[1.01]",
            !isDragging && !aiLocked && "border-muted-foreground/25 hover:border-violet-400 hover:bg-violet-50/30 dark:hover:bg-violet-950/10",
            aiLocked && "border-muted-foreground/20 bg-muted/30 opacity-60 cursor-not-allowed",
            uploading && "pointer-events-none opacity-60"
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => !uploading && !aiLocked && fileInputRef.current?.click()}
          data-testid="upload-dropzone"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg"
            onChange={handleFileSelect}
            className="hidden"
            data-testid="file-input"
          />

          {uploading ? (
            <div className="space-y-4">
              <Loader2 className="w-12 h-12 mx-auto animate-spin text-primary" />
              <div>
                <p className="text-lg font-medium">Processing document...</p>
                <p className="text-sm text-muted-foreground">
                  AI Buck is reading your document
                </p>
              </div>
              <div className="w-48 h-2 mx-auto bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
                <Upload className="w-8 h-8 text-primary" />
              </div>
              <div>
                <p className="text-lg font-medium">
                  Drop your document here
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Bills · Invoices · Receipts · Tax returns · W-9s · Contracts · Bank statements
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  or click to browse
                </p>
              </div>
              <div className="text-xs text-muted-foreground">
                Supported: PDF, PNG, JPG (max 50MB)
              </div>
            </div>
          )}
        </div>

        {/* Magic-demo CTAs — let visitors experience the Phase 2-4 flow
            without uploading anything of their own. Each spawns a real
            OCR job (pre-extracted via /api/ocr/demo) and lands the user
            on the appropriate AI surface. */}
        <div className="mt-6 pt-6 border-t" data-testid="ocr-demo-cta-row">
          <div className="flex items-center justify-center gap-2 text-xs uppercase tracking-wide text-muted-foreground mb-3">
            <Sparkles className="w-3.5 h-3.5 text-violet-600" />
            or try AI Buck with a sample document
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Button
              variant="outline"
              className="justify-start h-auto py-3"
              disabled={!!demoLoading || uploading}
              onClick={() => tryDemo('1040')}
              data-testid="demo-cta-1040"
            >
              {demoLoading === '1040' ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FileText className="w-4 h-4 mr-2 text-emerald-600" />
              )}
              <div className="text-left">
                <div className="font-semibold text-sm">Sample 1040</div>
                <div className="text-xs text-muted-foreground">Find missed deductions</div>
              </div>
            </Button>
            <Button
              variant="outline"
              className="justify-start h-auto py-3"
              disabled={!!demoLoading || uploading}
              onClick={() => tryDemo('w9')}
              data-testid="demo-cta-w9"
            >
              {demoLoading === 'w9' ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FileText className="w-4 h-4 mr-2 text-amber-600" />
              )}
              <div className="text-left">
                <div className="font-semibold text-sm">Sample W-9</div>
                <div className="text-xs text-muted-foreground">Create a 1099 vendor</div>
              </div>
            </Button>
            <Button
              variant="outline"
              className="justify-start h-auto py-3"
              disabled={!!demoLoading || uploading}
              onClick={() => tryDemo('contract')}
              data-testid="demo-cta-contract"
            >
              {demoLoading === 'contract' ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FileText className="w-4 h-4 mr-2 text-violet-600" />
              )}
              <div className="text-left">
                <div className="font-semibold text-sm">Sample Contract</div>
                <div className="text-xs text-muted-foreground">Summarize obligations</div>
              </div>
            </Button>
          </div>
        </div>

        {/* Recent scans — gives users a way to find their past documents
            from the Document Scanner without bouncing around the app. */}
        <div className="mt-6 pt-6 border-t" data-testid="recent-scans-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
              <Clock className="w-3.5 h-3.5" />
              Recent scans
            </div>
            <Button variant="ghost" size="sm" className="text-xs h-7" onClick={loadRecent} disabled={recentLoading}>
              {recentLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Refresh'}
            </Button>
          </div>
          {/* Filter chips — collapses long history by destination so firms
              processing many scans can find the right one quickly. */}
          <div className="flex flex-wrap gap-1.5 mb-3" data-testid="recent-scans-filter">
            {[
              { value: 'all',         label: 'All' },
              { value: 'unfiled',     label: 'Unfiled' },
              { value: 'tax_folder',  label: 'Tax' },
              { value: 'receipts',    label: 'Receipts' },
              { value: 'banking',     label: 'Banking' },
              { value: 'vendors',     label: 'Vendors' },
              { value: 'documents',   label: 'Docs' },
            ].map((chip) => (
              <button
                key={chip.value}
                type="button"
                onClick={() => setRecentFilter(chip.value)}
                className={cn(
                  "px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors",
                  recentFilter === chip.value
                    ? "bg-violet-600 text-white border-violet-600"
                    : "bg-card border-border hover:bg-muted/50 text-muted-foreground"
                )}
                data-testid={`recent-filter-${chip.value}`}
              >
                {chip.label}
              </button>
            ))}
          </div>
          {recentJobs.length === 0 ? (
            <p className="text-xs text-muted-foreground italic py-4 text-center bg-muted/30 rounded-lg" data-testid="recent-scans-empty">
              No scans match this filter yet.
            </p>
          ) : (
            <div className="space-y-1.5">
              {recentJobs.map((j) => {
                const dt = j.doc_type || j.document_type || 'unknown';
                const typeMeta = DOC_TYPE_CHOICES.find((c) => c.value === dt) || DOC_TYPE_CHOICES[0];
                const isProcessing = ['pending', 'processing', 'preparing'].includes(j.status);
                const filed = !!j.filed;
                const hasSplits = Array.isArray(j.splits) && j.splits.length > 1 && !j.auto_split_filed;
                const splitFiled = !!j.auto_split_filed;
                const filedDestMeta = j.filed_destination
                  ? FILING_DESTINATIONS.find((d) => d.value === j.filed_destination)
                  : null;
                return (
                  <div
                    key={j.job_id}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-card hover:bg-muted/40 transition-colors"
                    data-testid={`recent-scan-${j.job_id}`}
                  >
                    <button
                      type="button"
                      onClick={() => onUploadComplete(j.job_id)}
                      className="flex-1 flex items-center justify-between gap-3 text-left min-w-0"
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                        <div className="min-w-0">
                          <div className="font-medium text-sm truncate" title={j.filename}>
                            {j.filename || 'Untitled'}
                          </div>
                          <div className="text-[11px] text-muted-foreground truncate">
                            {j.extracted_name || 'No vendor detected'} · {typeMeta.label}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {hasSplits && (
                          <Badge
                            variant="outline"
                            className="text-[10px] bg-violet-50 dark:bg-violet-950/40 border-violet-300 dark:border-violet-800 text-violet-700 dark:text-violet-300"
                            data-testid={`recent-scan-splits-badge-${j.job_id}`}
                            title="This PDF contains multiple documents"
                          >
                            <Layers className="w-2.5 h-2.5 mr-0.5" />
                            {j.splits.length} docs
                          </Badge>
                        )}
                        {splitFiled && (
                          <Badge
                            variant="outline"
                            className="text-[10px] bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-900 text-emerald-700 dark:text-emerald-400"
                            data-testid={`recent-scan-split-filed-badge-${j.job_id}`}
                            title="Auto-split: each document was filed separately"
                          >
                            <Layers className="w-2.5 h-2.5 mr-0.5" />
                            Split filed
                          </Badge>
                        )}
                        {filed && filedDestMeta && (
                          <Badge
                            variant="outline"
                            className="text-[10px] bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-900 text-emerald-700 dark:text-emerald-400"
                            data-testid={`recent-scan-filed-badge-${j.job_id}`}
                          >
                            <CheckCircle className="w-2.5 h-2.5 mr-0.5" />
                            {filedDestMeta.shortLabel}
                          </Badge>
                        )}
                        {!filed && !splitFiled && (
                          <Badge
                            variant={isProcessing ? 'secondary' : (j.status === 'failed' ? 'destructive' : 'outline')}
                            className="text-[10px]"
                          >
                            {isProcessing ? (
                              <span className="flex items-center gap-1">
                                <Loader2 className="w-2.5 h-2.5 animate-spin" /> Processing
                              </span>
                            ) : (
                              (j.status || 'unknown').replace('_', ' ')
                            )}
                          </Badge>
                        )}
                      </div>
                    </button>
                    {/* Inline "auto-split" CTA — appears for multi-doc PDFs
                        that haven't been split yet. Lets the user file all
                        N docs in one click straight from the row. */}
                    {hasSplits && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-xs border-violet-300 text-violet-700 hover:bg-violet-50"
                        onClick={() => setSplitJob(j)}
                        data-testid={`recent-scan-split-${j.job_id}`}
                      >
                        <Wand2 className="w-3 h-3 mr-1" />
                        Split
                      </Button>
                    )}
                    {/* Per-row overflow menu — shown for every row (filed
                        OR unfiled). Filed rows get the full Move-to menu;
                        all rows get Delete. */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 shrink-0"
                          data-testid={`recent-scan-menu-${j.job_id}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        {filed && (
                          <>
                            <DropdownMenuLabel className="text-[10px] uppercase tracking-wide">Move to…</DropdownMenuLabel>
                            {FILING_DESTINATIONS.filter((d) => d.value !== j.filed_destination).map((d) => (
                              <DropdownMenuItem
                                key={d.value}
                                onClick={() => refileScan(j.job_id, d.value)}
                                data-testid={`refile-${j.job_id}-${d.value}`}
                              >
                                <ArrowRight className="w-3.5 h-3.5 mr-2 opacity-60" />
                                {d.shortLabel}
                              </DropdownMenuItem>
                            ))}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => openFolderPickerFor(j)}
                              data-testid={`refile-${j.job_id}-pick-folder`}
                            >
                              <Folder className="w-3.5 h-3.5 mr-2 opacity-60" />
                              Pick another folder…
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {/* For filed scans, "Open" jumps to the actual
                                Documents-Hub folder where the doc lives. */}
                            <DropdownMenuItem
                              onClick={() => {
                                if (j.filed_folder_id) {
                                  navigate(`/documents?space=firm&folder=${j.filed_folder_id}`);
                                } else {
                                  onUploadComplete(j.job_id);
                                }
                              }}
                              data-testid={`open-scan-${j.job_id}`}
                            >
                              <Eye className="w-3.5 h-3.5 mr-2 opacity-60" />
                              Open in Documents
                            </DropdownMenuItem>
                          </>
                        )}
                        <DropdownMenuItem onClick={() => onUploadComplete(j.job_id)}>
                          <FileText className="w-3.5 h-3.5 mr-2 opacity-60" />
                          View scan details
                        </DropdownMenuItem>
                        {hasSplits && (
                          <DropdownMenuItem
                            onClick={() => setSplitJob(j)}
                            data-testid={`recent-scan-split-menu-${j.job_id}`}
                          >
                            <Wand2 className="w-3.5 h-3.5 mr-2 opacity-60 text-violet-600" />
                            Auto-split &amp; file…
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => setDeleteCandidate(j)}
                          className="text-rose-600 focus:text-rose-700 focus:bg-rose-50 dark:focus:bg-rose-950/40"
                          data-testid={`recent-scan-delete-${j.job_id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-2" />
                          {(filed || splitFiled) ? 'Remove from queue' : 'Delete scan'}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CardContent>
    </Card>

    {/* Folder-picker dialog for "Move to any folder" action */}
    <Dialog open={!!folderPickerJob} onOpenChange={(o) => !o && setFolderPickerJob(null)}>
      <DialogContent data-testid="folder-picker-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Move scan to a folder</DialogTitle>
          <DialogDescription>
            Pick any firm folder. The scan will be moved out of its current location.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2 space-y-2">
          <Input
            placeholder="Search folders…"
            value={folderPickerSearch}
            onChange={(e) => setFolderPickerSearch(e.target.value)}
            data-testid="folder-picker-search"
          />
          <div className="max-h-72 overflow-y-auto rounded-lg border">
            {folderPickerLoading ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 mx-auto animate-spin" />
              </div>
            ) : availableFolders.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                No folders found.
              </div>
            ) : (
              availableFolders
                .filter((f) => !folderPickerSearch || (f.name || '').toLowerCase().includes(folderPickerSearch.toLowerCase()))
                .map((f) => (
                  <button
                    key={f.folder_id}
                    type="button"
                    onClick={() => moveToCustomFolder(f.folder_id)}
                    className="w-full text-left px-3 py-2 hover:bg-muted/50 border-b last:border-b-0 flex items-center gap-2"
                    data-testid={`folder-picker-option-${f.folder_id}`}
                  >
                    <Folder className="w-4 h-4 opacity-60" />
                    <span className="text-sm truncate">{f.name}</span>
                    {f.path && f.path !== f.name && (
                      <span className="text-[10px] text-muted-foreground truncate ml-auto">
                        {f.path}
                      </span>
                    )}
                  </button>
                ))
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Auto-split confirmation dialog — shows the detected splits and lets
        the user file them all in one click. Reads `splits` from the job. */}
    <Dialog open={!!splitJob} onOpenChange={(o) => !o && setSplitJob(null)}>
      <DialogContent data-testid="auto-split-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-violet-600" />
            We found {splitJob?.splits?.length ?? 0} documents in this PDF
          </DialogTitle>
          <DialogDescription>
            AI Buck spotted multiple document types in your packet. File each
            one to its own folder in a single click — the splits show below.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-72 overflow-y-auto space-y-1.5 my-2" data-testid="auto-split-list">
          {(splitJob?.splits || []).map((s, idx) => {
            const dt = s.doc_type || 'unknown';
            const typeMeta = DOC_TYPE_CHOICES.find((c) => c.value === dt) || DOC_TYPE_CHOICES[0];
            const destMeta = FILING_DESTINATIONS.find((d) => d.value === s.destination);
            return (
              <div
                key={idx}
                className="flex items-center gap-3 px-3 py-2 rounded-lg border bg-card"
                data-testid={`auto-split-item-${idx}`}
              >
                <FileText className="w-4 h-4 text-violet-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {typeMeta.label}
                    <span className="text-xs text-muted-foreground ml-2">
                      Pages {s.page_start}-{s.page_end}
                    </span>
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {s.text_preview || '—'}
                  </div>
                </div>
                <Badge
                  variant="outline"
                  className="text-[10px] bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-900 text-emerald-700 dark:text-emerald-400 shrink-0"
                  data-testid={`auto-split-dest-${idx}`}
                >
                  → {destMeta?.shortLabel || s.destination}
                </Badge>
              </div>
            );
          })}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setSplitJob(null)} disabled={splitFiling}>
            Cancel
          </Button>
          <Button
            onClick={() => autoSplitFile(splitJob)}
            disabled={splitFiling}
            className="bg-violet-600 hover:bg-violet-700"
            data-testid="auto-split-confirm-btn"
          >
            {splitFiling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
            File all {splitJob?.splits?.length ?? 0} documents
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Delete confirmation — wording adapts to filed vs unfiled. */}
    <AlertDialog open={!!deleteCandidate} onOpenChange={(o) => !o && setDeleteCandidate(null)}>
      <AlertDialogContent data-testid="recent-scan-delete-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {(deleteCandidate?.filed || deleteCandidate?.auto_split_filed)
              ? 'Remove from Recent Scans?'
              : 'Delete this scan?'}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {(deleteCandidate?.filed || deleteCandidate?.auto_split_filed) ? (
              <>
                This scan was already filed to your Documents Hub. Removing it
                from Recent Scans <strong>will not</strong> delete the document
                from its folder — it just hides this row from the queue.
              </>
            ) : (
              <>
                This scan hasn&apos;t been filed anywhere. Deleting will permanently
                remove the OCR job and the original file from CountBucks. This
                can&apos;t be undone.
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting} data-testid="recent-scan-delete-cancel">
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={deleteJob}
            disabled={deleting}
            className="bg-rose-600 hover:bg-rose-700 text-white"
            data-testid="recent-scan-delete-confirm"
          >
            {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
            {(deleteCandidate?.filed || deleteCandidate?.auto_split_filed) ? 'Remove from queue' : 'Delete scan'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
};
